import { Box, Center, Flex, Link, Progress, Spinner, Stack, Text } from "@chakra-ui/react";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  BidV2Status,
  SourcingEventStatus,
  type BuyerBidV2DetailDto,
  type EvalCellDto,
  type SourcingEventV2Dto,
  useGetEvaluationQuery,
  useGetSourcingEventQuery,
  useListBidV2SForEventQuery,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { scorableAwardRows } from "../../../../common/award-criteria/scorableAwardRows";
import { urls } from "../../../../urls";
import { AwardCriteriaAnswers } from "../../../sourcing-events/bid-readonly/AwardCriteriaAnswers";
import { isScoringOpen } from "../bidAccess";
import { isEvaluationLocked } from "../qualification/stage";
import { AwardCriterionPriceCard } from "./AwardCriterionPriceCard";
import { AwardCriterionScoreFields } from "./AwardCriterionScoreFields";
import { useBuyerAwardCriterionScoreEditor } from "./useBuyerAwardCriterionScoreEditor";
import { criterionTypeLabelKey } from "../../../sourcing-events/buyer/wizard/utils/publicProcurementOptions";

interface Props {
  detail: BuyerBidV2DetailDto;
  headerStats?: ReactNode;
}

const NO_CELLS: EvalCellDto[] = [];

/**
 * The supplier's award-criterion answers together with the buyer's scoring of them. One supplier at a
 * time -- the bid is already chosen by the page you are on -- so each criterion gets its answer, its
 * points and its justification in one place rather than a cell in the comparison grid.
 */
export const AwardCriteriaTab = ({ detail, headerStats }: Props) => {
  const { t } = useTranslation();
  const eventId = detail.bid.forEvent;
  const bidId = detail.bid.id;
  const criteria = detail.awardCriteria ?? [];

  const { data: event, isLoading: isEventLoading } = useGetSourcingEventQuery({ eventId });
  // The bid's scores live in ITS stage's evaluation -- a round bid is scored within the round.
  const { data: bidsList } = useListBidV2SForEventQuery({ eventId });
  const round = bidsList?.bids.find((candidate) => candidate.id === bidId)?.forNegotiationRound;
  const evaluation = useGetEvaluationQuery({ eventId, round: round ?? undefined });

  const evaluatedBid = evaluation.data?.bids.find((bid) => bid.bidId === bidId);
  const cells = useMemo(() => evaluatedBid?.cells ?? NO_CELLS, [evaluatedBid]);
  const editor = useBuyerAwardCriterionScoreEditor(eventId, bidId, cells);

  const rows = scorableAwardRows(criteria, (type) => t(criterionTypeLabelKey(type)));
  const cellsById = new Map(cells.map((cell) => [cell.criterionId, cell] as const));
  // A price row is computed from the tendered price, so it is never one of the rows to work through.
  const scorable = rows.filter((row) => !row.isPrice);
  const assessed = scorable.filter((row) => editor.scoreOf(row.id).points != null).length;

  const { selectedOrg } = useLoggedInWithOrgContextState();
  const isRejected = detail.bid.status === BidV2Status.Rejected;
  const isUnderEvaluation = event != null && isScoringOpen(event, selectedOrg);
  // Without the evaluation we do not know what is already scored. Showing empty grade strips over a
  // bid that has been graded would invite the evaluator to score it a second time, on top of work
  // they cannot see — so a failed or absent evaluation reads, it never writes.
  const hasEvaluation = !evaluation.isError && evaluatedBid != null;
  const readOnly = !isUnderEvaluation || isRejected || !hasEvaluation;

  // Both queries decide whether this is a scoring view or a reading one, so neither half is shown
  // before the other: the fields would appear read-only and then turn editable under the cursor.
  if (evaluation.isLoading || isEventLoading) {
    return (
      <Center py="16">
        <Spinner color="smPrimary" />
      </Center>
    );
  }

  return (
    <Stack spacing="6">
      <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" px="5" py="4">
        {headerStats ?? (
          <Flex align="center" gap="3">
            <Progress
              value={scorable.length > 0 ? (assessed / scorable.length) * 100 : 0}
              size="sm"
              rounded="full"
              colorScheme={assessed === scorable.length && scorable.length > 0 ? "green" : "teal"}
              bg="smBackgroundSecondary"
              flex="1"
            />
            <Text color="smMuted" fontSize="sm" whiteSpace="nowrap">
              {t("{{count}} of {{total}} reviewed", { count: assessed, total: scorable.length })}
            </Text>
          </Flex>
        )}
        {readOnly && (
          <ScoringLockedNotice
            eventId={eventId}
            event={event}
            isRejected={isRejected}
            evaluationState={evaluation.isError ? "error" : evaluatedBid ? "loaded" : "missing"}
          />
        )}
      </Box>

      <AwardCriteriaAnswers
        criteria={criteria}
        responses={detail.bid.awardCriterionResponses ?? []}
        price={detail.bid.price}
        answerLabel={String(t("Supplier's answer"))}
        notAnsweredLabel={String(t("Not answered by supplier"))}
        renderPriceRow={(row) => (
          <AwardCriterionPriceCard
            // A price criterion names the criterion, not what is scored; only a sub-criterion under
            // it says something the heading above has not already said.
            title={(row.parentId != null && row.name?.trim()) || String(t("Tendered price"))}
            price={detail.bid.price}
            points={cellsById.get(row.id)?.points ?? null}
            priceModel={event?.eventData?.priceModel}
            isPriceModelKnown={event != null}
          />
        )}
        renderRowFooter={(row) => (
          <AwardCriterionScoreFields
            points={editor.scoreOf(row.id).points}
            comment={editor.scoreOf(row.id).comment}
            isReadOnly={readOnly}
            onPointsChange={(value) => editor.setPoints(row.id, value)}
            onCommentChange={(value) => editor.setComment(row.id, value)}
          />
        )}
      />
    </Stack>
  );
};

// Once the evaluation is over the scores are part of the record and stay put.
const AFTER_EVALUATION: SourcingEventStatus[] = [
  SourcingEventStatus.Evaluated,
  SourcingEventStatus.AwardDecision,
  SourcingEventStatus.Awarded,
  SourcingEventStatus.Cancelled,
  SourcingEventStatus.Closed,
  SourcingEventStatus.Completed,
];

/**
 * Why this bid cannot be scored right now, and where to go about it. Saying only that scoring
 * belongs to the evaluation leaves the reader on a dead end: what opens the evaluation differs by
 * procedure, and for a single-stage one it is not on this page at all.
 */
const ScoringLockedNotice = ({
  eventId,
  event,
  isRejected,
  evaluationState,
}: {
  eventId: string;
  event: SourcingEventV2Dto | undefined;
  isRejected: boolean;
  evaluationState: "loaded" | "error" | "missing";
}) => {
  const { t } = useTranslation();
  const eventUrl = urls.v2.sourcingEvents.view.go(eventId);

  const notice = (): { text: string; to?: string; linkLabel?: string } => {
    if (isRejected) return { text: String(t("Tilbudet er avvist og skal ikke evalueres videre.")) };
    // Without the procurement we do not know why it is locked, so we do not guess at a reason.
    if (!event) return { text: String(t("The procurement could not be loaded.")) };
    if (evaluationState === "error") return { text: String(t("Evaluation could not be loaded.")) };
    if (evaluationState === "missing") {
      return { text: String(t("The bid is not included in the evaluation.")) };
    }
    if (AFTER_EVALUATION.includes(event.status)) {
      return { text: String(t("The evaluation is finished - the scores can no longer be changed.")) };
    }
    // SM-867: a single-stage procurement leaves PUBLISHED by completing its qualification, so there
    // is no «Start evaluering» to press — the way in is the Kvalifisering tab.
    if (isEvaluationLocked(event)) {
      return {
        text: String(t("Scoring opens once the qualification is complete.")),
        to: `${eventUrl}?tab=qualification`,
        linkLabel: String(t("Go to Qualification")),
      };
    }
    return {
      text: String(t("The evaluation has not started yet.")),
      to: `${eventUrl}?tab=evaluation`,
      linkLabel: String(t("Go to Evaluation")),
    };
  };

  const { text, to, linkLabel } = notice();

  return (
    <Flex align="center" gap="2" flexWrap="wrap" mt="3">
      <Text color="smMuted" fontSize="sm">
        {text}
      </Text>
      {to && (
        <Link as={RouterLink} to={to} color="smPrimary" fontSize="sm" fontWeight="medium">
          {linkLabel}
        </Link>
      )}
    </Flex>
  );
};
